import React, { useState, useEffect, useMemo, memo } from 'react';
import { QuestionCard } from './QuestionCard';
import { MultipleChoiceQuestion } from './MultipleChoiceQuestion';
import { FillInQuestion } from './FillInQuestion';
import { TrueFalseQuestion } from './TrueFalseQuestion';
import { MultiSelectQuestion } from './MultiSelectQuestion';
import { MatchingQuestion } from './MatchingQuestion';
import { DragDropQuestion } from './DragDropQuestion';
import { Button, Alert, Badge, Modal } from 'react-bootstrap';
import { useQuestionInput, useHints, useFeedback } from '@hooks';
import { tagMasteryCalculator } from '@services';
import { QUESTION_REASON } from '@config/constants';
import { RichText } from '@common/RichText';
import { isFillInAnswerCorrect } from '@utils/fillInAnswerMatching';

/**
 * Interactive Question Component
 * Handles all question types with hints and feedback
 * Now displays adaptive selection reason badges
 * Includes HamTestOnline remediation triggers (3+ consecutive errors)
 * Supports unlimited multi-attempt learning
 */
function InteractiveQuestionContent({ 
  question, 
  userId,
  courseId,
  onSubmit, 
  onNext,
  onContinue = () => {},
  showExplanation = false,
  disabled = false,
  reason = null,  // Why was this question selected?
  currentSectionId = null,  // Current section being studied
  questionSectionId = null,  // Section this question is from
  course = null,  // Course data for section lookup
  timesShown = 0,  // How many times this question has been displayed
  sectionMastery = 0, // Section mastery percentage
  isRoundComplete = false, // Is this the last question in the round?
}) {
  const { value, handleChange, reset } = useQuestionInput('', question?.id);
  const normalizedQuestionTags = useMemo(
    () => normalizeQuestionTags(question?.tags),
    [question?.tags],
  );
  const stableHints = useMemo(() => question?.hints ?? [], [question?.id]);
  const { revealedHints, hasMoreHints, revealNextHint, hintsUsedCount } = useHints(stableHints);
  const { feedback, isVisible, showFeedback, clearFeedback } = useFeedback();
  const [attempts, setAttempts] = useState(0);
  const [tagMasteries, setTagMasteries] = useState({}); // Track tag mastery percentages
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false); // Track if last answer was correct
  const [incorrectAnswers, setIncorrectAnswers] = useState([]); // Track all incorrect answers for display
  const [fiftyFiftyUsed, setFiftyFiftyUsed] = useState(false); // Track if 50/50 lifeline was used
  const [disabledOptions, setDisabledOptions] = useState([]); // Track options disabled by 50/50
  const [isSkipped, setIsSkipped] = useState(false); // Track if question was skipped
  const [reviewContentUsed, setReviewContentUsed] = useState(false); // Track if review content was previewed
  const [showReviewContent, setShowReviewContent] = useState(false); // Toggle review content panel
  const [masteryRefreshNonce, setMasteryRefreshNonce] = useState(0); // Forces mastery refetch after submits

  const submitWithMasteryRefresh = (payload) => {
    if (!onSubmit) return;

    Promise.resolve(onSubmit(payload))
      .catch((error) => {
        console.warn('[InteractiveQuestion] Submission failed', error);
      })
      .finally(() => {
        setMasteryRefreshNonce((prev) => prev + 1);
      });
  };

  // Fetch tag mastery when question changes
  useEffect(() => {
    let cancelled = false;

    const fetchTagMasteries = async () => {
      if (!userId || !courseId || normalizedQuestionTags.length === 0) {
        if (!cancelled) setTagMasteries({});
        return;
      }

      try {
        const allMasteries = await tagMasteryCalculator.getAllTagMasteries(userId, courseId);
        const masteryByTag = Object.fromEntries(
          (allMasteries || [])
            .map((item) => [
              normalizeTagId(item?.tagId),
              toPercent(item?.finalMastery ?? item?.percentage ?? item?.mastery),
            ])
            .filter(([tagId]) => !!tagId),
        );

        if (!cancelled) {
          const masteries = {};
          for (const tag of normalizedQuestionTags) {
            masteries[tag.id] = masteryByTag[tag.id] ?? 0;
          }
          setTagMasteries(masteries);
        }
      } catch (error) {
        console.warn('[InteractiveQuestion] Failed to load tag masteries', error);
        if (!cancelled) setTagMasteries({});
      }
    };

    fetchTagMasteries();

    return () => {
      cancelled = true;
    };
  }, [question?.id, userId, courseId, normalizedQuestionTags, masteryRefreshNonce]);

  const handleSubmitAnswer = async () => {
    const isCorrect = validateAnswer(question, value);
    const newAttempts = attempts + 1;
    
    setAttempts(newAttempts);
    setIsAnswerCorrect(isCorrect);
    
    // Track incorrect answers in array
    if (!isCorrect && !incorrectAnswers.includes(JSON.stringify(value))) {
      setIncorrectAnswers([...incorrectAnswers, value]);
    }
    
    showFeedback(isCorrect, question.explanation);
    
    submitWithMasteryRefresh({
      questionId: question.id,
      userAnswer: value,
      isCorrect,
      hintsUsed: hintsUsedCount,
      attempts: newAttempts,  // Track all attempts
      fiftyFiftyUsed, // Pass 50/50 lifeline usage
      reviewContentUsed, // Pass review content usage for scoring
    });
  };

  const handleSkipQuestion = async () => {
    setIsSkipped(true);
    
    // Submit as skipped with 0 points
    submitWithMasteryRefresh({
      questionId: question.id,
      userAnswer: value || '[SKIPPED]',
      isCorrect: false,
      skipped: true,
      hintsUsed: hintsUsedCount,
      attempts: attempts + 1,
      fiftyFiftyUsed,
      reviewContentUsed,
    });
    
    // Show skip feedback
    showFeedback(false, `Question skipped. The correct answer was: ${question.correctAnswer || 'not provided'}`);
    
    // Move to next question after a short delay
    setTimeout(() => {
      if (isRoundComplete) {
        onContinue();
      } else {
        handleNextQuestion();
      }
    }, 2000);
  };

  /**
   * Auto-submit answer when value changes (user selects an option)
   * This replaces the need for a submit button
   */
  const handleAnswerChange = async (newValue) => {
    handleChange(newValue);
    
    // Wait for state to update before checking
    setTimeout(async () => {
      const isCorrect = validateAnswer(question, newValue);
      const newAttempts = attempts + 1;
      
      setAttempts(newAttempts);
      setIsAnswerCorrect(isCorrect);
      
      showFeedback(isCorrect, question.explanation);
      
      submitWithMasteryRefresh({
        questionId: question.id,
        userAnswer: newValue,
        isCorrect,
        hintsUsed: hintsUsedCount,
        attempts: newAttempts,
        fiftyFiftyUsed, // Pass 50/50 lifeline usage
        reviewContentUsed, // Pass review content usage for scoring
      });
    }, 0);
  };

  const handleNextQuestion = () => {
    reset();
    clearFeedback();
    setAttempts(0);
    setIsAnswerCorrect(false);
    setFiftyFiftyUsed(false);
    setDisabledOptions([]);
    setIsSkipped(false);
    setIncorrectAnswers([]);
    setReviewContentUsed(false);
    setShowReviewContent(false);
    if (onNext) {
      onNext();
    }
  };

  /**
   * Check if question type should auto-submit on selection
   * Only multiple-choice and true-false should auto-submit
   * Others (fill-in, multi-select, drag-drop, matching) need explicit submit
   */
  const shouldAutoSubmit = () => {
    return question.type === 'multiple-choice' || question.type === 'true-false';
  };

  /**
   * Check if question type needs a submit button
   */
  const needsSubmitButton = () => {
    return ['fill-in', 'multi-select', 'drag-drop', 'matching'].includes(question.type);
  };

  /**
   * Handle 50/50 lifeline - disable two wrong answers
   * Reduces maximum possible points to 50%
   */
  const handleFiftyFifty = () => {
    if (fiftyFiftyUsed || question.type !== 'multiple-choice') return;
    
    // Find all incorrect options
    const incorrectOptions = question.options.filter(opt => opt !== question.correctAnswer);
    
    // Randomly select two to disable (or all incorrect if less than 2)
    const toDisable = [];
    const shuffled = [...incorrectOptions].sort(() => Math.random() - 0.5);
    toDisable.push(...shuffled.slice(0, Math.min(2, shuffled.length)));
    
    setDisabledOptions(toDisable);
    setFiftyFiftyUsed(true);
  };

  // Milestone 9 (PRD): Keyboard shortcuts — Enter for Next, 1-4 to select an answer
  // Placed after all handlers so closures correctly capture the latest function references.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept when user is typing in a form field
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      // Enter → advance to next question / continue once answer is resolved
      if (e.key === 'Enter' && (isAnswerCorrect || isSkipped)) {
        e.preventDefault();
        if (isRoundComplete) {
          onContinue();
        } else {
          handleNextQuestion();
        }
        return;
      }

      // 1–4 → select answer for auto-submit question types
      if (['1', '2', '3', '4'].includes(e.key) && !isAnswerCorrect && !isSkipped && !disabled) {
        const idx = parseInt(e.key, 10) - 1;

        if (question?.type === 'multiple-choice') {
          const option = question.options?.[idx];
          if (option !== undefined && !disabledOptions.includes(option)) {
            e.preventDefault();
            handleAnswerChange(option);
          }
        } else if (question?.type === 'true-false') {
          const tfOptions = ['True', 'False'];
          const option = tfOptions[idx];
          if (option !== undefined) {
            e.preventDefault();
            handleAnswerChange(option);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isAnswerCorrect,
    isSkipped,
    isRoundComplete,
    disabled,
    question?.type,
    question?.options,
    disabledOptions,
    onContinue,
    handleNextQuestion,
    handleAnswerChange,
  ]);

  const renderQuestionInput = () => {
    const commonProps = {
      question,
      value,
      onChange: shouldAutoSubmit() ? handleAnswerChange : handleChange,
      disabled: disabled || isAnswerCorrect, // Disable after correct answer
      showCorrect: isAnswerCorrect, // Show correct answer after correct answer
      incorrectAnswers, // Track all incorrect answers for display
      disabledOptions, // Options disabled by 50/50
    };


    switch (question.type) {
      case 'multiple-choice':
        return <MultipleChoiceQuestion {...commonProps} />;
      case 'true-false':
        return <TrueFalseQuestion {...commonProps} />;
      case 'fill-in':
        return <FillInQuestion {...commonProps} />;
      case 'multi-select':
        return <MultiSelectQuestion {...commonProps} />;
      case 'matching':
        return <MatchingQuestion {...commonProps} />;
      case 'drag-drop':
        return <DragDropQuestion {...commonProps} />;
      default:
        return <Alert variant="warning">Question type not supported: {question.type}</Alert>;
    }
  };

  // Determine if question is from a different section
  const questionSection = course?.sections?.find(s => s.id === questionSectionId);
  const reviewSection = course?.sections?.find(s => s.id === (questionSectionId || currentSectionId));
  const isDifferentSection = questionSectionId && currentSectionId && questionSectionId !== currentSectionId;

  return (
    <QuestionCard
      question={question}
      headerContent={
        <div className="mb-3">
          {reason && (
            <>
              <Badge
                bg={getReasonBadgeVariant(reason)}
                className="p-2"
                title={getReasonTooltip(reason, isDifferentSection, questionSection, timesShown)}
              >
                {getReasonLabel(reason, isDifferentSection, questionSection, timesShown)}
              </Badge>
              {/* F7 (PRD): "Review from [Section Title]" badge when question is from a different section */}
              {isDifferentSection && questionSection && (
                <Badge bg="secondary" className="p-2 ms-2">
                  📚 Review from {questionSection.title}
                </Badge>
              )}
            </>
          )}
          {!reason && (
            <small className="text-danger"><strong>⚠️ No reason provided</strong></small>
          )}
        </div>
      }
    >

      {renderQuestionInput()}

      {/* Tag mastery display (below answers) */}
      {(questionSection || normalizedQuestionTags.length > 0) && (
        <div className="mt-2 p-2 bg-light border rounded">
          <small className="text-muted">
            <span>
              {questionSection && `Section [${sectionMastery}%]`}
              {normalizedQuestionTags.length > 0 && normalizedQuestionTags.map((tag, index) => (
                <span key={tag.id}>
                  {(questionSection || index > 0) && ' | '}
                  {tag.label} [{Math.round(tagMasteries[tag.id] ?? 0)}%]
                </span>
              ))}
            </span>
          </small>
        </div>
      )}

      {/* Hints Section */}
      {revealedHints.length > 0 && (
        <div className="hints-section mt-3">
          {revealedHints.map((hint, index) => (
            <Alert key={index} variant="info" className="mb-2">
              <strong>Hint {index + 1}:</strong> {hint}
            </Alert>
          ))}
        </div>
      )}

      {/* Review Content Modal */}
      <Modal
        show={showReviewContent}
        onHide={() => setShowReviewContent(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Section Review Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!reviewSection && (
            <p className="text-muted small mb-0">No review content available for this section.</p>
          )}

          {reviewSection && (
            <>
              {reviewSection.contentText && (
                <RichText as="div" className="small mb-3" value={reviewSection.contentText} />
              )}

              {(reviewSection.examples || [])
                .filter((example) => example.showInReview !== false)
                .map((example, index) => (
                  <div key={example.id || index} className="mb-3 ps-2 border-start border-info">
                    {example.title && <strong className="small d-block mb-1">{example.title}</strong>}

                    {example.content && (
                      <RichText as="div" className="small mb-2" value={example.content} />
                    )}

                    {!example.content && example.code && (
                      <pre className="small mb-2 bg-light border rounded p-2"><code>{example.code}</code></pre>
                    )}

                    {!example.content && !example.code && example.explanation && (
                      <RichText as="div" className="small mb-0" value={example.explanation} />
                    )}
                  </div>
                ))}

              {!reviewSection.contentText
                && !(reviewSection.examples || []).some((example) => example.showInReview !== false) && (
                <p className="text-muted small mb-0">No review content available.</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <small className="text-warning">
            ⚠️ Using review content will slightly reduce your score if you answer correctly.
          </small>
          <Button variant="secondary" onClick={() => setShowReviewContent(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Feedback Section */}
      {isVisible && feedback && (
        <Alert variant={feedback.isCorrect ? 'success' : 'danger'} className="mt-3">
          <strong>{feedback.isCorrect ? '✓ Correct!' : isSkipped ? '⊘ Skipped' : '✗ Incorrect, try again'}</strong>
          {showExplanation && feedback.explanation && feedback.isCorrect && (
            <RichText as="p" className="mb-0 mt-2" value={feedback.explanation} />
          )}
          {/* Show explanation for skipped questions or when we want to reveal the answer */}
          {isSkipped && feedback.explanation && (
            <RichText as="p" className="mb-0 mt-2" value={feedback.explanation} />
          )}
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <div className="d-flex gap-2">
          {hasMoreHints && !isAnswerCorrect && !isSkipped && (
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={revealNextHint}
            >
              Show Hint ({hintsUsedCount}/{question.hints?.length || 0})
            </Button>
          )}
          
          {/* 50/50 Lifeline Button - Only for multiple choice */}
          {question.type === 'multiple-choice' && !isAnswerCorrect && !fiftyFiftyUsed && !isSkipped && (
            <Button 
              variant="outline-warning" 
              size="sm"
              onClick={handleFiftyFifty}
              disabled={disabled}
              title="Remove two incorrect answers (reduces points to 50%)"
            >
              🎯 50/50
            </Button>
          )}
          
          {/* Show 50/50 used indicator */}
          {fiftyFiftyUsed && (
            <Badge bg="warning" className="d-flex align-items-center px-2">
              🎯 50/50 Used (50% points)
            </Badge>
          )}

          {/* Review Content Button - shows section content for reference (applies score penalty) */}
          {!isAnswerCorrect && !isSkipped && (
            <Button
              variant={reviewContentUsed ? 'outline-warning' : 'outline-info'}
              size="sm"
              onClick={() => {
                setShowReviewContent(true);
                if (!reviewContentUsed) setReviewContentUsed(true);
              }}
              title="Preview section review content (slightly reduces score if answered correctly)"
            >
              {reviewContentUsed ? '📖 Review (Used)' : '📖 Review Content'}
            </Button>
          )}
          
          {/* Skip Button - All question types when not answered */}
          {!isAnswerCorrect && !isSkipped && (
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={handleSkipQuestion}
              disabled={disabled}
              title="Skip this question (0 points)"
            >
              Skip (0 pts)
            </Button>
          )}
        </div>
        <div className="d-flex gap-2 align-items-center">
          {attempts > 0 && !isSkipped && (
            <small className="text-muted">Attempt {attempts}</small>
          )}
          
          {isSkipped && (
            <Badge bg="secondary" className="d-flex align-items-center px-2">
              Skipped
            </Badge>
          )}
          
          {/* Show Submit button for complex question types */}
          {needsSubmitButton() && !isAnswerCorrect && !isSkipped && (
            <Button 
              variant="primary" 
              onClick={handleSubmitAnswer}
              disabled={!value || disabled}
            >
              Submit Answer
            </Button>
          )}
          
          {/* Show "Next Question" or "Next" button when answer is correct or skipped */}
          {(isAnswerCorrect || isSkipped) && (
            <Button 
              variant="success" 
              onClick={isRoundComplete ? onContinue : handleNextQuestion}
            >
              {isRoundComplete ? 'Next →' : 'Next Question →'}
            </Button>
          )}
        </div>
      </div>
    </QuestionCard>
  );
}

/**
 * Memoized Interactive Question Component
 * Only re-renders when critical props change (question.id, value, disabled)
 * This prevents parent component re-renders from triggering expensive re-calculations
 */
export const InteractiveQuestion = memo(InteractiveQuestionContent, (prevProps, nextProps) => {
  // Re-render if these critical props change
  return (
    prevProps.question?.id === nextProps.question?.id &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.userId === nextProps.userId &&
    prevProps.courseId === nextProps.courseId &&
    prevProps.currentSectionId === nextProps.currentSectionId
  );
});

function getOptionKey(option, index) {
  if (option == null) return index;
  if (typeof option === 'string' || typeof option === 'number') return option;
  return option.id ?? option.value ?? option.key ?? option.text ?? index;
}

function normalizeMultiSelectAnswer(question, answers) {
  if (!Array.isArray(answers)) return [];
  const options = Array.isArray(question?.options) ? question.options : [];

  return answers.map(answer => {
    const matchIndex = options.findIndex((opt, idx) => (
      opt?.id === answer ||
      opt?.value === answer ||
      opt?.key === answer ||
      opt?.text === answer ||
      idx === answer
    ));

    if (matchIndex >= 0) {
      return getOptionKey(options[matchIndex], matchIndex);
    }

    return answer;
  });
}

function normalizeTagId(tag) {
  if (typeof tag === 'string' || typeof tag === 'number') {
    return String(tag).trim();
  }

  if (tag && typeof tag === 'object') {
    const candidate = tag.id ?? tag.tagId ?? tag.value ?? tag.key ?? tag.name;
    return candidate == null ? '' : String(candidate).trim();
  }

  return '';
}

function getTagLabel(tag, fallbackId) {
  if (typeof tag === 'string' || typeof tag === 'number') {
    return String(tag);
  }

  if (tag && typeof tag === 'object') {
    const candidate = tag.label ?? tag.name ?? fallbackId;
    return candidate == null ? fallbackId : String(candidate);
  }

  return fallbackId;
}

function normalizeQuestionTags(tags) {
  if (!Array.isArray(tags)) return [];

  const seen = new Set();
  const normalized = [];

  for (const tag of tags) {
    const tagId = normalizeTagId(tag);
    if (!tagId || seen.has(tagId)) continue;

    seen.add(tagId);
    normalized.push({
      id: tagId,
      label: getTagLabel(tag, tagId),
    });
  }

  return normalized;
}

function toPercent(value) {
  if (value == null || value === '') return 0;

  const numeric = typeof value === 'string'
    ? Number.parseFloat(value.replace('%', '').trim())
    : Number(value);

  if (!Number.isFinite(numeric)) return 0;

  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return Math.max(0, Math.min(100, percent));
}

function validateAnswer(question, userAnswer) {
  if (!userAnswer) return false;

  switch (question.type) {
    case 'multiple-choice':
      return userAnswer === question.correctAnswer;
    case 'true-false': {
      const normalized = String(userAnswer).trim().toLowerCase();
      const correct = String(question.correctAnswer).trim().toLowerCase();
      return normalized === correct;
    }
    case 'fill-in':
      return isFillInAnswerCorrect(userAnswer, question.correctAnswer);
    case 'multi-select': {
      // userAnswer is array of selected option IDs
      // correctAnswer is array of correct option IDs
      if (!Array.isArray(userAnswer) || !Array.isArray(question.correctAnswer)) return false;
      if (userAnswer.length !== question.correctAnswer.length) return false;

      const normalizedUser = normalizeMultiSelectAnswer(question, userAnswer);
      const normalizedCorrect = normalizeMultiSelectAnswer(question, question.correctAnswer);

      const sortedUser = [...normalizedUser].sort();
      const sortedCorrect = [...normalizedCorrect].sort();
      return sortedUser.every((val, idx) => val === sortedCorrect[idx]);
    }
    case 'matching': {
      // userAnswer is object mapping left IDs to right IDs
      // correctAnswer is object with correct pairs
      if (typeof userAnswer !== 'object' || typeof question.correctAnswer !== 'object') return false;
      
      const userKeys = Object.keys(userAnswer);
      const correctKeys = Object.keys(question.correctAnswer);
      
      if (userKeys.length !== correctKeys.length) return false;
      
      return userKeys.every(leftId => 
        userAnswer[leftId] === question.correctAnswer[leftId]
      );
    }
    case 'drag-drop': {
      // userAnswer is array of item IDs in user's order
      // correctAnswer is array of item IDs in correct order
      if (!Array.isArray(userAnswer) || !Array.isArray(question.correctAnswer)) return false;
      if (userAnswer.length !== question.correctAnswer.length) return false;
      
      return userAnswer.every((val, idx) => val === question.correctAnswer[idx]);
    }
    default:
      return false;
  }
}

/**
 * Get badge color variant based on selection reason
 */
function getReasonBadgeVariant(reason) {
  switch (reason) {
    case QUESTION_REASON.DECAY:
      return 'warning';        // Orange - needs refreshing
    case QUESTION_REASON.TIME:
      return 'info';           // Blue - time-based review
    case QUESTION_REASON.STRUGGLE:
      return 'danger';         // Red - needs extra help
    case QUESTION_REASON.PREVIEW:
      return 'secondary';      // Gray - preview mode
    case QUESTION_REASON.RESUME:
      return 'primary';        // Blue - resuming session
    case QUESTION_REASON.CURRENT:
    default:
      return 'success';        // Green - current module
  }
}

/**
 * Get user-friendly label for selection reason
 */
function getReasonLabel(reason, isDifferentSection, questionSection, timesShown = 0) {
  switch (reason) {
    case QUESTION_REASON.DECAY:
      return '📚 Refresh';
    case QUESTION_REASON.TIME:
      return '⏰ Spaced Review';
    case QUESTION_REASON.STRUGGLE:
      return '💪 Practice';
    case QUESTION_REASON.PREVIEW:
      return '👀 Preview';
    case QUESTION_REASON.RESUME:
      return '↩️ Resuming';
    case QUESTION_REASON.CURRENT:
    default:
      // For current section questions:
      // Show "New" only if never seen before (timesShown === 0)
      // Show "Review" if from different section OR if already seen
      if (timesShown === 0 && !isDifferentSection) {
        return '✨ New';
      }
      return '🔄 Review';
  }
}

/**
 * Get tooltip explanation for selection reason
 */
function getReasonTooltip(reason, isDifferentSection, questionSection, timesShown = 0) {
  const sectionContext = isDifferentSection && questionSection 
    ? ` This question is from "${questionSection.title}".`
    : '';

  switch (reason) {
    case QUESTION_REASON.DECAY:
      return `This question was selected because your mastery has decayed - time for a refresher!${sectionContext}`;
    case QUESTION_REASON.TIME:
      return `This is a spaced repetition review to strengthen your memory.${sectionContext}`;
    case QUESTION_REASON.STRUGGLE:
      return `You've been struggling with this topic - extra practice will help!${sectionContext}`;
    case QUESTION_REASON.PREVIEW:
      return `Preview this concept before it becomes part of the core content.${sectionContext}`;
    case QUESTION_REASON.RESUME:
      return `Resuming your previous learning session.${sectionContext}`;
    case QUESTION_REASON.CURRENT:
    default:
      if (timesShown === 0 && !isDifferentSection) {
        return `New question from current module.${sectionContext}`;
      } else if (isDifferentSection) {
        return `Review question from a previous module to reinforce your learning.${sectionContext}`;
      } else {
        return `Reviewing a question you've seen before to strengthen your mastery.${sectionContext}`;
      }
  }
}
